import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, PartyPopper, Users, Calendar, Clock, MessageSquare, CreditCard, Share2, Download, CheckCircle, MapPin } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: { color: '#E6D7C3', fontFamily: '"Helvetica Neue", Helvetica, sans-serif', fontSmoothing: 'antialiased', fontSize: '16px', '::placeholder': { color: '#8A7355' } },
    invalid: { color: '#fa755a', iconColor: '#fa755a' },
  },
};

function PaymentForm({ bookingData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/bottle/purchase`, bookingData);
      const result = await stripe.confirmCardPayment(data.client_secret, { payment_method: { card: elements.getElement(CardElement) } });
      if (result.error) {
        setError(result.error.message);
      } else {
        const confirmRes = await axios.post(`${API}/bottle/confirm-payment/${data.transaction_id}`);
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      {error && <div style={{ padding: '12px', background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '8px', color: '#fca5a5', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
      <button type="submit" disabled={!stripe || processing} style={{ width: '100%', padding: '16px', background: processing ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: processing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <CreditCard size={20} />
        {processing ? 'Processing...' : `Pay $${bookingData.estimatedPrice}`}
      </button>
    </form>
  );
}

const SendBottlePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: venues, 2: bottles, 3: booking details, 4: payment, 5: success
  const [venues, setVenues] = useState([]);
  const [bottles, setBottles] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedBottle, setSelectedBottle] = useState(null);
  const [formData, setFormData] = useState({ date: '', time: '', partySize: 4, specialRequests: '', recipientType: 'qr', recipientMember: '', recipientInfo: '', message: '', estimatedPrice: 0 });
  const [aiResponse, setAiResponse] = useState('');
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bottle/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error('Error fetching venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBottles = async (venueId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bottle/venues/${venueId}/menu`);
      setBottles(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error fetching bottles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVenueSelect = (venue) => {
    setSelectedVenue(venue);
    fetchBottles(venue.id);
  };

  const handleBottleSelect = (bottle) => {
    setSelectedBottle(bottle);
    setStep(3);
  };

  const handleAIBooking = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bottle/ai-booking`, {
        venueName: selectedVenue.name,
        partySize: formData.partySize,
        bottleName: selectedBottle.name,
        bottlePrice: selectedBottle.price,
        tableMinimum: selectedVenue.table_minimum
      });
      setAiResponse(data.message);
      setFormData(prev => ({ ...prev, estimatedPrice: data.estimatedPrice }));
      setTimeout(() => setConfirmationSent(true), 1500);
      setStep(4);
    } catch (error) {
      alert('AI Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setQrData(data);
    setStep(5);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `bottle-qr-${qrData.unique_code}.png`;
    link.href = url;
    link.click();
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Bottle Service at ${qrData.venue_name}`, text: `I booked bottle service for you! ${formData.message}`, url: qrData.qr_url });
      } catch (err) { console.error('Error sharing:', err); }
    } else {
      navigator.clipboard.writeText(qrData.qr_url);
      alert('Link copied to clipboard!');
    }
  };

  const groupBottlesByCategory = (bottles) => {
    return bottles.reduce((acc, bottle) => {
      if (!acc[bottle.category]) acc[bottle.category] = [];
      acc[bottle.category].push(bottle);
      return acc;
    }, {});
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button onClick={() => step === 1 ? navigate('/gifts') : step === 2 ? setStep(1) : step === 3 ? setStep(2) : step === 4 ? setStep(3) : setStep(1)} style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {step === 5 ? 'BOOKING CONFIRMED!' : 'BOTTLE SERVICE'}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Progress */}
        {step < 5 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', justifyContent: 'center' }}>
            {[1, 2, 3, 4].map(s => (
              <div key={s} style={{ width: '40px', height: '4px', background: s <= step ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.2)', borderRadius: '2px' }} />
            ))}
          </div>
        )}

        {/* Step 1: Select Venue */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Select Club</h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading venues...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {venues.map(venue => (
                  <div key={venue.id} onClick={() => handleVenueSelect(venue)} style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', gap: '16px' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}>
                    {venue.photo && (
                      <img src={venue.photo} alt={venue.name} style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>{venue.name}</h3>
                      <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '4px' }}>
                        <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        {venue.address}, {venue.city}
                      </p>
                      <span style={{ display: 'inline-block', padding: '4px 8px', background: 'rgba(200, 168, 130, 0.2)', color: '#C8A882', borderRadius: '4px', fontSize: '12px', textTransform: 'capitalize' }}>
                        Table Minimum: ${venue.table_minimum}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Bottle */}
        {step === 2 && selectedVenue && (
          <div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>Selected Venue</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedVenue.name}</h3>
              <p style={{ color: '#8A7355', fontSize: '14px' }}>Table Minimum: ${selectedVenue.table_minimum}</p>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Bottle Menu</h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading bottles...</p>
            ) : (
              Object.entries(groupBottlesByCategory(bottles)).map(([category, categoryBottles]) => (
                <div key={category} style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', color: '#D7B56D', marginBottom: '12px', textTransform: 'uppercase', fontWeight: 'bold' }}>{category}</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {categoryBottles.map(bottle => (
                      <div key={bottle.id} onClick={() => handleBottleSelect(bottle)} style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ fontSize: '16px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>{bottle.name}</h4>
                            <p style={{ fontSize: '13px', color: '#8A7355' }}>{bottle.size}</p>
                          </div>
                          <span style={{ fontSize: '18px', color: '#D7B56D', fontWeight: 'bold' }}>${bottle.price}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Step 3: Booking Details */}
        {step === 3 && selectedBottle && (
          <div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <p style={{ color: '#A57C33', fontSize: '13px' }}>{selectedVenue.name}</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedBottle.name}</h3>
              <p style={{ color: '#D7B56D', fontSize: '16px', fontWeight: 'bold' }}>${selectedBottle.price}</p>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Booking Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Time</label>
                <input type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Party Size</label>
              <input type="number" min="1" value={formData.partySize} onChange={(e) => setFormData({...formData, partySize: parseInt(e.target.value)})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Special Requests (Optional)</label>
              <textarea placeholder="Birthday, celebration, seating preferences..." value={formData.specialRequests} onChange={(e) => setFormData({...formData, specialRequests: e.target.value})} rows="3" style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '12px', fontSize: '14px' }}>Send To</label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'qr'})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'qr' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'qr' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Anyone (QR Code)</button>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'member'})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'member' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'member' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>NearU Member</button>
              </div>
              {formData.recipientType === 'member' ? (
                <input type="text" placeholder="Enter member username" value={formData.recipientMember} onChange={(e) => setFormData({...formData, recipientMember: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              ) : (
                <input type="text" placeholder="Recipient name (optional)" value={formData.recipientInfo} onChange={(e) => setFormData({...formData, recipientInfo: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              )}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Personal Message</label>
              <textarea placeholder="Add a message..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows="3" style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none', resize: 'vertical' }} />
            </div>
            <button onClick={handleAIBooking} disabled={loading || !formData.date} style={{ width: '100%', padding: '16px', background: loading ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <MessageSquare size={20} />
              {loading ? 'AI Processing...' : 'Get Booking Confirmation'}
            </button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && (
          <div>
            {confirmationSent && (
              <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CheckCircle size={24} color="#22c55e" />
                <div>
                  <p style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '4px' }}>✓ Confirmation Sent!</p>
                  <p style={{ color: '#A57C33', fontSize: '14px' }}>Venue has been notified of your booking</p>
                </div>
              </div>
            )}
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ color: '#D7B56D', fontSize: '18px', marginBottom: '12px', fontWeight: 'bold' }}>AI Concierge Response</h3>
              <p style={{ color: '#E6D7C3', lineHeight: '1.6', marginBottom: '16px' }}>{aiResponse}</p>
              <div style={{ borderTop: '1px solid #A57C33', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#A57C33' }}>Venue:</span>
                  <span style={{ color: '#E6D7C3', fontWeight: 'bold' }}>{selectedVenue.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#A57C33' }}>Bottle:</span>
                  <span style={{ color: '#E6D7C3' }}>{selectedBottle.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#A57C33' }}>Date & Time:</span>
                  <span style={{ color: '#E6D7C3' }}>{formData.date} at {formData.time}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#A57C33' }}>Party Size:</span>
                  <span style={{ color: '#E6D7C3' }}>{formData.partySize} guests</span>
                </div>
                <div style={{ borderTop: '1px solid #A57C33', marginTop: '12px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>Total:</span>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '20px' }}>${formData.estimatedPrice}</span>
                </div>
              </div>
            </div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Complete Payment</h2>
            <Elements stripe={stripePromise}>
              <PaymentForm bookingData={{ ...formData, venueName: selectedVenue.name, bottlePreference: selectedBottle.name }} onSuccess={handlePaymentSuccess} />
            </Elements>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && qrData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #C8A882 0%, #A8846B 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <PartyPopper size={40} color="#2C1B13" />
            </div>
            <h2 style={{ fontSize: '28px', color: '#E6D7C3', marginBottom: '12px', fontFamily: 'Playfair Display, serif' }}>Booking Confirmed!</h2>
            <p style={{ color: '#A57C33', marginBottom: '32px', fontSize: '16px' }}>Your VIP table is reserved</p>
            <div style={{ background: 'white', padding: '24px', borderRadius: '16px', display: 'inline-block', marginBottom: '24px' }}>
              <QRCodeCanvas id="qr-code-canvas" value={qrData.qr_url} size={250} level="H" />
            </div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Booking Details</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>Bottle Service at {qrData.venue_name}</h3>
              <p style={{ color: '#D7B56D' }}>{selectedBottle.name}</p>
              <p style={{ color: '#E6D7C3', marginTop: '4px' }}>{qrData.booking_date} at {qrData.booking_time}</p>
              <p style={{ color: '#8A7355', fontSize: '14px', marginTop: '8px' }}>Code: {qrData.unique_code}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button onClick={shareQRCode} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Share2 size={20} /> Share
              </button>
              <button onClick={downloadQRCode} style={{ flex: 1, padding: '14px', background: 'rgba(230, 215, 195, 0.1)', color: '#E6D7C3', border: '1px solid #A57C33', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Download size={20} /> Download
              </button>
            </div>
            <button onClick={() => navigate('/gifts')} style={{ width: '100%', padding: '14px', background: 'none', color: '#A57C33', border: 'none', fontSize: '16px', cursor: 'pointer' }}>Back to Gifts</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendBottlePage;
